/**
 * Caches the dynamic import of `@datadog/browser-rum` as a shared in-flight promise.
 *
 * Extracted from any one caller so it can be reused by other RUM instrumentation without each
 * paying for its own import, and so the module-level cache it needs is discoverable in one place
 * rather than living inside an unrelated hook.
 */

let datadogRumImport: Promise<typeof import('@datadog/browser-rum')> | undefined

/**
 * Returns the `@datadog/browser-rum` module, importing it once and sharing that import across
 * concurrent and later calls.
 *
 * Cached as a promise, not the resolved module, so concurrent callers (e.g. two RUM measurements
 * in flight at once) share the same in-flight import instead of each starting their own. Only a
 * successful import is cached (via the `.catch` below, not a post-await reassignment, which a
 * rejected promise would need and which lint flags as a race): a transiently blocked import stays
 * uncached, so a later call can retry it instead of being stuck failing for the rest of the
 * session.
 */
export function importDatadogRum(): Promise<typeof import('@datadog/browser-rum')> {
  if (!datadogRumImport) {
    const attempt = import('@datadog/browser-rum')

    datadogRumImport = attempt
    void attempt.catch(() => {
      if (datadogRumImport === attempt) datadogRumImport = undefined
    })
  }

  return datadogRumImport
}

/** Test-only: clears the cached import so each test starts from a clean state. */
export function resetDatadogRumImport(): void {
  datadogRumImport = undefined
}
